import loadVips from './vipsRuntime'

/** 与 sharp().webp() 默认质量一致。 */
const STORED_WEBP_QUALITY = 80
/** 同一张图里用多线程。一次仍只做一张，避免多张 2048 图叠在内存里。 */
const VIPS_THREADS = 4
/** 压缩搜索轮数。质量参数不变；无损图的像素不变，只是文件略大、编码更快。 */
const WEBP_EFFORT = 1
const COVER_PAD = 30
const COVER_SIZE = 300
const GRAY = [221, 221, 221]
const CLEAR = [0, 0, 0, 0]

export type CropRect = { x: number; y: number; w: number; h: number }

export type PreparedPsdAssets = {
  crop: CropRect
  original: Blob
  square: Blob
  component: Blob
  cover?: Blob
}

type VipsImage = {
  width: number
  height: number
  delete: () => void
  copy: (options?: { interpretation?: string }) => VipsImage
  premultiply: () => VipsImage
  unpremultiply: () => VipsImage
  cast: (format: string) => VipsImage
  resize: (
    scale: number,
    options?: { kernel?: string; vscale?: number },
  ) => VipsImage
  embed: (
    x: number,
    y: number,
    width: number,
    height: number,
    options?: { extend?: string; background?: number[] },
  ) => VipsImage
  extractArea: (
    left: number,
    top: number,
    width: number,
    height: number,
  ) => VipsImage
  composite: (
    overlay: VipsImage,
    mode: string,
    options?: { x?: number; y?: number },
  ) => VipsImage
  flatten: (options?: { background?: number[] }) => VipsImage
  writeToBuffer: (format: string, options?: Record<string, unknown>) => Uint8Array
}

type VipsApi = {
  concurrency: (count?: number) => number
  Image: {
    newFromMemory: (
      data: Uint8Array,
      width: number,
      height: number,
      bands: number,
      format: string,
    ) => VipsImage
    black: (width: number, height: number, options?: { bands?: number }) => VipsImage
  }
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

/** 与服务端 visibleLayerOnCanvas 同一套换算，不读像素 */
const visibleLayer = (params: {
  left: number
  top: number
  srcWidth: number
  srcHeight: number
  canvas: number
  baseSize: number
  /** 3:4 等非方形画布的高，缺省等于宽。宽高按同一比例缩放。 */
  canvasHeight?: number
  baseHeight?: number
  /** 在方图上实际采纳的范围。竖屏 PSD 用它把两侧留白留空。 */
  clip?: { left: number; top: number; right: number; bottom: number }
}) => {
  const left = Math.round(params.left)
  const top = Math.round(params.top)
  const srcWidth = Math.round(params.srcWidth)
  const srcHeight = Math.round(params.srcHeight)
  const canvas = Math.round(params.canvas)
  const baseSize = Math.round(params.baseSize)
  const canvasHeight = Math.round(params.canvasHeight ?? canvas)
  const baseHeight = Math.round(params.baseHeight ?? baseSize)
  if (
    canvas <= 0 ||
    baseSize <= 0 ||
    canvasHeight <= 0 ||
    baseHeight <= 0 ||
    srcWidth <= 0 ||
    srcHeight <= 0
  ) {
    throw new Error('invalid layer place')
  }
  const clipLeft = clamp(Math.round(params.clip?.left ?? 0), 0, canvas)
  const clipTop = clamp(Math.round(params.clip?.top ?? 0), 0, canvasHeight)
  const clipRight = clamp(Math.round(params.clip?.right ?? canvas), 0, canvas)
  const clipBottom = clamp(
    Math.round(params.clip?.bottom ?? canvasHeight),
    0,
    canvasHeight,
  )
  const visLeft = Math.max(clipLeft, left)
  const visTop = Math.max(clipTop, top)
  const visRight = Math.min(clipRight, left + srcWidth)
  const visBottom = Math.min(clipBottom, top + srcHeight)
  if (visRight <= visLeft || visBottom <= visTop) {
    throw new Error('layer is outside the canvas')
  }
  const scale = baseSize / canvas
  const x = clamp(Math.round(visLeft * scale), 0, baseSize - 1)
  const y = clamp(Math.round(visTop * scale), 0, baseHeight - 1)
  const right = clamp(Math.round(visRight * scale), x + 1, baseSize)
  const bottom = clamp(Math.round(visBottom * scale), y + 1, baseHeight)
  return {
    crop: { x, y, w: right - x, h: bottom - y },
    src: {
      left: visLeft - left,
      top: visTop - top,
      width: visRight - visLeft,
      height: visBottom - visTop,
    },
    destLeft: visLeft,
    destTop: visTop,
    canvas,
    baseSize,
    canvasHeight,
    baseHeight,
  }
}

export const releaseCanvas = (canvas?: HTMLCanvasElement | null) => {
  if (!canvas) return
  canvas.width = 0
  canvas.height = 0
}

let loading: Promise<VipsApi> | null = null
let gate: Promise<void> = Promise.resolve()

const getVips = () => {
  if (!loading) {
    loading = Promise.resolve()
      .then(() => loadVips() as unknown as Promise<VipsApi>)
      .then((vips) => {
        vips.concurrency(VIPS_THREADS)
        return vips
      })
      .catch((error: unknown) => {
        loading = null
        const message = error instanceof Error ? error.message : String(error)
        throw new Error(`清晰缩放组件加载失败：${message}`)
      })
  }
  return loading
}

const withVips = async <T>(task: (vips: VipsApi) => T): Promise<T> => {
  const vips = await getVips()
  const previous = gate
  let openGate = () => {}
  gate = new Promise<void>((resolve) => {
    openGate = resolve
  })
  await previous
  try {
    return task(vips)
  } finally {
    openGate()
  }
}

const readPiece = (
  image: HTMLCanvasElement,
  src: { left: number; top: number; width: number; height: number },
) => {
  const ctx = image.getContext('2d')
  if (!ctx) throw new Error('canvas context unavailable')
  const data = ctx.getImageData(src.left, src.top, src.width, src.height)
  return new Uint8Array(data.data)
}

const webpBlob = (image: VipsImage, options: Record<string, unknown>) => {
  const bytes = image.writeToBuffer('.webp', { effort: WEBP_EFFORT, ...options })
  return new Blob([bytes.slice()], { type: 'image/webp' })
}

/** 用 libvips 做出原图、方图、组件图和封面，缩放方式与后端 sharp 一致。 */
export const preparePsdAssets = async (params: {
  image: HTMLCanvasElement
  left: number
  top: number
  canvasSize: number
  baseSize: number
  canvasHeight?: number
  baseHeight?: number
  withCover: boolean
  autoCover: number
  clip?: { left: number; top: number; right: number; bottom: number }
}): Promise<PreparedPsdAssets> => {
  const place = visibleLayer({
    left: params.left,
    top: params.top,
    srcWidth: params.image.width,
    srcHeight: params.image.height,
    canvas: params.canvasSize,
    baseSize: params.baseSize,
    canvasHeight: params.canvasHeight,
    baseHeight: params.baseHeight,
    clip: params.clip,
  })
  const { crop, src } = place

  return withVips((vips) => {
    const pixels = readPiece(params.image, src)
    const live = new Set<VipsImage>()
    const own = (image: VipsImage) => {
      live.add(image)
      return image
    }
    const drop = (image: VipsImage) => {
      if (!live.delete(image)) return
      image.delete()
    }
    const asRgba = (image: VipsImage) => {
      const copied = own(image.copy({ interpretation: 'srgb' }))
      drop(image)
      return copied
    }
    // sharp 缩小带透明的图时会先乘透明度再缩，避免透明处的黑色渗进边缘。
    const resizeCubic = (image: VipsImage, scale: number, vscale?: number) => {
      const premulFloat = own(image.premultiply())
      const premul = own(premulFloat.cast('uchar'))
      drop(premulFloat)
      const resized = own(
        premul.resize(scale, {
          ...(vscale === undefined ? {} : { vscale }),
          kernel: 'cubic',
        }),
      )
      drop(premul)
      const straightFloat = own(resized.unpremultiply())
      drop(resized)
      const straight = own(straightFloat.cast('uchar'))
      drop(straightFloat)
      return straight
    }
    const resizeFill = (image: VipsImage, width: number, height: number) => {
      const resized = resizeCubic(
        image,
        width / image.width,
        height / image.height,
      )
      if (resized.width === width && resized.height === height) return resized
      const framed = own(
        resized.embed(
          0,
          0,
          Math.max(resized.width, width),
          Math.max(resized.height, height),
          { extend: 'background', background: CLEAR },
        ),
      )
      const exact = own(framed.extractArea(0, 0, width, height))
      drop(resized)
      drop(framed)
      return exact
    }
    const fitInside = (image: VipsImage, size: number) => {
      const scale = Math.min(size / image.width, size / image.height)
      let resized = resizeCubic(image, scale)
      if (resized.width > size || resized.height > size) {
        const cropped = own(
          resized.extractArea(
            0,
            0,
            Math.min(size, resized.width),
            Math.min(size, resized.height),
          ),
        )
        drop(resized)
        resized = cropped
      }
      const left = Math.floor((size - resized.width) / 2)
      const top = Math.floor((size - resized.height) / 2)
      const framed = own(
        resized.embed(left, top, size, size, {
          extend: 'background',
          background: CLEAR,
        }),
      )
      drop(resized)
      return framed
    }

    try {
      const piece = asRgba(
        own(
          vips.Image.newFromMemory(pixels, src.width, src.height, 4, 'uchar'),
        ),
      )
      const component = resizeFill(piece, crop.w, crop.h)
      const componentBlob = webpBlob(component, { lossless: true })

      const squareBase = asRgba(
        own(vips.Image.black(place.baseSize, place.baseHeight, { bands: 4 })),
      )
      const square = own(
        squareBase.composite(component, 'over', { x: crop.x, y: crop.y }),
      )
      drop(squareBase)
      const squareBlob = webpBlob(square, { Q: STORED_WEBP_QUALITY })
      drop(component)
      drop(square)

      const originalBase = asRgba(
        own(vips.Image.black(place.canvas, place.canvasHeight, { bands: 4 })),
      )
      const original = own(
        originalBase.composite(piece, 'over', {
          x: place.destLeft,
          y: place.destTop,
        }),
      )
      drop(originalBase)
      const originalBlob = webpBlob(original, { Q: STORED_WEBP_QUALITY })
      drop(original)

      let cover: Blob | undefined
      if (params.withCover) {
        const padded = own(
          piece.embed(
            COVER_PAD,
            COVER_PAD,
            piece.width + COVER_PAD * 2,
            piece.height + COVER_PAD * 2,
            { extend: 'background', background: CLEAR },
          ),
        )
        let framed = fitInside(padded, COVER_SIZE)
        drop(padded)
        if (params.autoCover === 2) {
          const flat = own(framed.flatten({ background: GRAY }))
          drop(framed)
          framed = flat
        }
        cover = webpBlob(framed, { Q: STORED_WEBP_QUALITY })
        drop(framed)
      }
      drop(piece)

      return {
        crop,
        original: originalBlob,
        square: squareBlob,
        component: componentBlob,
        cover,
      }
    } finally {
      live.forEach((image) => image.delete())
      live.clear()
    }
  })
}
